/**
 * The widget, from `init` to a rendered exchange.
 *
 * Four states, and three of them are refusals worth showing rather than a spinner:
 *
 *   - **waiting** - the host has not posted a token yet, which is normal for a moment.
 *   - **refused** - nobody allowlisted spoke. Almost always a host origin missing from `config.json`, and the
 *     honest thing is to say so: a rep looking at a permanent spinner reports "the widget is broken".
 *   - **no exchange** - a token arrived without one. The host is supposed to pass it, and the panel cannot
 *     guess: the token is scoped server-side, so asking for the wrong exchange would just be refused.
 *   - **ready** - the exchange screen.
 *
 * The token never appears in this file, in a URL, or in storage. It lives in the ref inside `useHostSession`
 * and is read at request time, so a refresh mid-upload swaps it without a re-render.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { ApiClient, ExchangeView } from '@iqz-ddds/components';
import type { ExchangeActions } from '@iqz-ddds/components';
import { runtimeConfig, type WidgetConfig } from './config';
import { useHostSession, useReportedHeight } from './embed/useHostSession';
import { RepToolsFrame } from './RepToolsFrame';

function directoryFrameFromLocation(): string | undefined {
  const raw = new URL(globalThis.location.href).searchParams.get('directoryFrame');
  if (raw === null || raw.trim() === '') return undefined;

  try {
    const parsed = new URL(raw);
    return parsed.toString();
  } catch {
    return undefined;
  }
}

type EmbeddedSurface = 'secure_documents' | 'rep_tools';

function surfaceFromLocation(): EmbeddedSurface {
  const raw = new URL(globalThis.location.href).searchParams.get('surface');
  if (raw === 'rep_tools' || raw === 'rep-tools') return 'rep_tools';
  return 'secure_documents';
}

export function App(): React.JSX.Element {
  // Styles are injected by `main.tsx`, the same place the prospect surface does it.
  const [config, setConfig] = useState<WidgetConfig | undefined>(undefined);
  useEffect(() => {
    queueMicrotask(() => setConfig(runtimeConfig));
  }, []);

  return config === undefined ? (
    <div className="ddds-page ddds-embedded">
      <p className="ddds-empty">Connecting.</p>
    </div>
  ) : <Embedded config={config} />;
}

/**
 * Split from `App` so the handshake starts only once the allowlist is known.
 *
 * `useHostSession` attaches a `message` listener and checks each origin against that list, so starting it with
 * an empty one would drop the first `init` - the one the host sends immediately - and the panel would sit in
 * `waiting` until a refresh that never comes.
 */
function Embedded({ config }: { readonly config: WidgetConfig }): React.JSX.Element {
  /*
   * The two actions, once the view has handed them out.
   *
   * A ref rather than state: this is a handle to call, not something rendered, and putting it in state
   * would re-render the tree the moment the view registered - which on a panel holding an in-flight
   * upload is exactly the re-render `useHostSession` goes to such lengths to avoid.
   *
   * A host that sets `chrome: 'none'` draws its own buttons and drives them through `iqz-ddds:action`;
   * see `widgetInitMessageSchema`. Undefined until the view mounts, so an action arriving before the
   * exchange has loaded is dropped rather than queued - the host's button simply does nothing that
   * once, which is better than firing into a view that is not there.
   */
  const actions = useRef<ExchangeActions | undefined>(undefined);

  const session = useHostSession({
    allowedOrigins: config.hostOrigins,
    onAction: (action) => {
      if (action === 'request') actions.current?.request();
      if (action === 'send') actions.current?.send();
    },
  });
  const frame = useRef<HTMLDivElement>(null);
  const directoryFrameUrl = useMemo(() => directoryFrameFromLocation(), []);
  const surface = useMemo(() => surfaceFromLocation(), []);
  // No allowlist argument since DG.38: the height goes out through the session, to the one target the
  // allowlist already approved, so a host suppressing its referrer is sized like any other.
  useReportedHeight(session, frame);

  const describeFailure = (err: unknown): { detail: string; reference?: string } => {
    const detail = err instanceof Error ? err.message : 'Something went wrong.';
    const reference = (err as { reference?: string }).reference;
    return { detail, ...(reference === undefined ? {} : { reference }) };
  };

  const client = useMemo(
    () =>
      new ApiClient({
        baseUrl: config.apiBaseUrl,
        // Section 13's second credential. The widget presents a brokered token, never a provider bearer.
        scheme: 'Widget',
        lapsedMessage: 'This panel needs to be reloaded by the page containing it.',
        token: () => session.token(),
        /*
         * A 401 asks the host for a replacement and waits briefly for one to arrive.
         *
         * The wait is the awkward part: `postMessage` is one-way, so there is no promise to await. Polling the
         * ref for a short window is the honest implementation - a token either turns up or it does not, and
         * `SessionLapsedError` is the answer when it does not.
         *
         * Reaching here at all means `expiring` did not do its job, since that fires a full minute early. The
         * likely causes are a host that ignored it and a clock disagreement, and both are worth surviving.
         */
        refresh: async () => {
          const before = session.token();
          session.requestRefresh();

          for (let waited = 0; waited < 5_000; waited += 100) {
            await new Promise((resolve) => setTimeout(resolve, 100));
            const now = session.token();
            if (now !== undefined && now !== before) return now;
          }
          return undefined;
        },
      }),
    [config.apiBaseUrl, session],
  );

  if (session.state.status === 'waiting') {
    return (
      <div className="ddds-page ddds-embedded" ref={frame}>
        <p className="ddds-empty">Connecting.</p>
      </div>
    );
  }

  if (session.state.status === 'refused') {
    return (
      <div className="ddds-page ddds-embedded" ref={frame}>
        <p className="ddds-notice" role="alert">
          {session.state.detail}
        </p>
      </div>
    );
  }

  if (surface === 'rep_tools') {
    return (
      <div ref={frame}>
        <RepToolsFrame client={client} onFailure={describeFailure} />
      </div>
    );
  }

  if (session.state.exchangeId === undefined) {
    return (
      <div className="ddds-page ddds-embedded" ref={frame}>
        <p className="ddds-notice" role="alert">
          This panel was opened without an exchange to show.
        </p>
      </div>
    );
  }

  return (
    <div ref={frame}>
      {directoryFrameUrl !== undefined && (
        <div className="ddds-page ddds-embedded" style={{ paddingBottom: '0.75rem' }}>
          <iframe
            title="Directory tools"
            src={directoryFrameUrl}
            style={{ width: '100%', minHeight: '26rem', border: 0, borderRadius: '8px' }}
          />
        </div>
      )}
      {/* `ddds-embedded` because a panel is not a page: full width, no bottom gutter. See surface.ts. */}
      <ExchangeView
        client={client}
        exchangeId={session.state.exchangeId}
        onError={session.reportError}
        className="ddds-page ddds-embedded"
        chrome={session.state.chrome}
        /*
         * The host's two layout preferences, straight from `init`.
         *
         * `tabs` arrives already defaulted to horizontal, because the contract promises that fallback and
         * `useHostSession` is the one place that reads the message. `view` arrives undefined when the host
         * has no opinion, and the view then chooses - deciding here would pre-empt it.
         */
        tabs={session.state.tabs}
        view={session.state.view}
        /*
         * How often the panel re-reads the exchange, from the runtime config rather than compiled in.
         *
         * A customer's own number: an embedded panel sitting open all day beside a CRM record is a
         * different load profile from the standalone application, and 0 turns polling off entirely.
         */
        pollSeconds={config.pollSeconds}
        documentRequestMaxLength={config.documentRequestMaxLength}
        /*
         * A takeover rather than an overlay, and it is geometry rather than taste.
         *
         * This frame is sized to its own content by `iqz-ddds:resize`, so its viewport is the whole
         * frame: a centred `position: fixed` dialog in a 1200px frame lands 600px down, below the fold
         * of whatever the CRM is showing. Hiding the panels and putting the form in their place makes
         * the frame resize to the form, which puts the dialog inside what the rep can already see -
         * without asking the host to scroll anything, and so without a fourth message in the protocol.
         */
        presentation="takeover"
        onActions={(handles) => {
          actions.current = handles;
        }}
      />
    </div>
  );
}
